import { Button } from 'UI';
import { CourseDto, CourseDtoTypeEnum } from 'api/generated';
import { accentForSlug, cdnCoverForSlug } from 'components/Modals/BundleBuilderModal/bundleBuilderConfig';
import React, { FC, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';
import { typography } from 'styles/general/typography';
import { getBreadcrumbsString, handleWordsDeclination } from 'utils';

/** Short English forms for the card's stats line. The shared pluralForms read as "tests questions". */
const statForms = {
  chapter: ['chapter', 'chapters', 'chapters'],
  question: ['question', 'questions', 'questions'],
  flashcards: ['flashcard deck', 'flashcard decks', 'flashcard decks'],
  mnemonics: ['mnemonic deck', 'mnemonic decks', 'mnemonic decks'],
};

/** Long enough that three clamped lines hide something worth a "Read more". */
const LONG_DESCRIPTION = 150;

const statsLine = (course: CourseDto) =>
  getBreadcrumbsString(
    [
      { value: course.amountChaptersAtStudyGuide, forms: statForms.chapter },
      { value: course.amountQuestions, forms: statForms.question },
      { value: course.amountFlashcardsPacks, forms: statForms.flashcards },
      { value: course.amountMnemonicCardsPacks, forms: statForms.mnemonics },
    ]
      .filter(({ value }) => typeof value === 'number' && value > 0)
      .map(({ value, forms }) => ({
        value: value.toLocaleString('en-US'),
        label: handleWordsDeclination(value, forms),
      })),
  );

/**
 * "NASM CPT" -> "NASM", "Sport course" -> "SC". Study systems are named after their certifying body,
 * so the leading acronym is the most recognisable mark when a course has no icon of its own.
 */
const monogram = (name: string) => {
  const words = name.trim().split(/\s+/);
  if (/^[A-Z0-9]{2,4}$/.test(words[0] ?? '')) return words[0];
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

const Thumbnail: FC<{ course: CourseDto }> = ({ course }) => {
  if (course.type === CourseDtoTypeEnum.Certificate) {
    const cover = course.coverImageUrl || cdnCoverForSlug(course.slug);
    if (cover) {
      return (
        <CoverThumb $accent={accentForSlug(course.slug)}>
          <img src={cover} alt="" loading="lazy" />
        </CoverThumb>
      );
    }
  }

  if (course.courseIconUrl) {
    return (
      <IconThumb>
        <img src={course.courseIconUrl} alt="" loading="lazy" />
      </IconThumb>
    );
  }

  return <MonogramThumb aria-hidden>{monogram(course.name)}</MonogramThumb>;
};

const Description: FC<{ text?: string }> = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!text) return null;

  const isLong = text.length > LONG_DESCRIPTION;

  return (
    <>
      <DescriptionText $clamped={isLong && !isExpanded}>{text}</DescriptionText>
      {isLong && (
        <ReadMore type="button" aria-expanded={isExpanded} onClick={() => setIsExpanded((value) => !value)}>
          {isExpanded ? 'Show less' : 'Read more'}
        </ReadMore>
      )}
    </>
  );
};

type CourseCardProps = {
  course: CourseDto;
  /** On the learner's dashboard as a trial: still to be bought, so listed, but marked. */
  isTrial?: boolean;
  onGet: (course: CourseDto) => void;
  /** Present for certifications the bundle builder sells: puts this one in the box above. */
  onAddToBundle?: () => void;
};

export const CourseCard: FC<CourseCardProps> = ({ course, isTrial, onGet, onAddToBundle }) => {
  const stats = useMemo(() => statsLine(course), [course]);

  return (
    <Card>
      <CardHead>
        <Thumbnail course={course} />
        <CardTitleBlock>
          <CardTitle>
            {course.name}
            {isTrial && <TrialTag>On trial</TrialTag>}
          </CardTitle>
          {stats && <Stats>{stats}</Stats>}
        </CardTitleBlock>
      </CardHead>

      <Description text={course.description} />

      <CardFooter>
        {onAddToBundle && (
          <BundleLink type="button" onClick={onAddToBundle}>
            Bundle &amp; save
          </BundleLink>
        )}
        <GetButton size="small" onClick={() => onGet(course)}>
          Get
        </GetButton>
      </CardFooter>
    </Card>
  );
};

const Card = styled.article`
  ${({ theme: { colors } }) => css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
    padding: 20px;
    border-radius: 16px;
    background: ${colors.neutrals[11]};
    border: 1px solid ${colors.neutrals[8]};
    transition: box-shadow 0.15s ease, border-color 0.15s ease;

    &:hover {
      border-color: ${colors.neutrals[7]};
      box-shadow: ${colors.shadow[2]};
    }

    ${respondToWidth.sm`
      padding: 16px;
    `}
  `}
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

const CardTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CardTitle = styled.h4`
  margin: 0;
  ${typography.body_large_bold_16};
  color: ${({ theme: { colors } }) => colors.neutrals[1]};
  overflow-wrap: anywhere;
`;

const TrialTag = styled.span`
  ${({ theme: { colors } }) => css`
    display: inline-block;
    margin-left: 8px;
    padding: 2px 6px;
    border-radius: 4px;
    vertical-align: middle;
    font-size: 10px;
    font-weight: 800;
    line-height: 1.3;
    text-transform: uppercase;
    color: ${colors.system.green};
    background: ${colors.system.lightGreen};
  `}
`;

const Stats = styled.span`
  ${typography.footnote_regular_12};
  color: ${({ theme: { colors } }) => colors.neutrals[3]};
`;

const thumbBase = css`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const CoverThumb = styled.span<{ $accent: string }>`
  ${thumbBase};
  width: 44px;
  aspect-ratio: 363 / 470;
  border-radius: 5px;
  box-shadow: 0 4px 12px ${({ $accent }) => $accent}33;

  img {
    object-fit: cover;
  }
`;

const IconThumb = styled.span`
  ${thumbBase};
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme: { colors } }) => colors.neutrals[10]};

  img {
    object-fit: contain;
    padding: 6px;
  }
`;

const MonogramThumb = styled.span`
  ${({ theme: { colors } }) => css`
    ${thumbBase};
    width: 48px;
    height: 48px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: ${colors.primary[1]};
    background: ${colors.primary[6]};
    border: 1px solid ${colors.primary[5]};
  `}
`;

const DescriptionText = styled.p<{ $clamped: boolean }>`
  ${({ theme: { colors }, $clamped }) => css`
    margin: 0;
    ${typography.body_basic_regular_14};
    color: ${colors.neutrals[2]};
    white-space: pre-line;

    ${$clamped &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
  `}
`;

const ReadMore = styled.button`
  align-self: flex-start;
  margin-top: -6px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  ${typography.subhead_semibold_13};
  color: ${({ theme: { colors } }) => colors.primary[1]};
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: auto;
  padding-top: 4px;
`;

const BundleLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  ${typography.subhead_semibold_13};
  color: ${({ theme: { colors } }) => colors.primary[1]};

  &:hover {
    text-decoration: underline;
  }
`;

const GetButton = styled(Button)`
  width: auto;
  min-width: 96px;
`;
